/**
 * Аннигилятор повторных http запросов, выполняемых через axios и fetch.
 *
 * Аннигилятор в первую очередь необходим для случаев получения данных в ситуациях когда http запросы могут быть долгими.
 * В общем случае, когда требуется получить данные по API и в момент ожидания ответа изменяется состояние приложения так,
 * что вновь необходимо запросить данные по тому же API, но с другими условиями, тогда возникает необходимость отмены предыдущего запроса,
 * чтобы его ответ не успел прийти и принести уже неактуальные данные.
 *
 * @see https://www.codetinkerer.com/2019/01/14/cancel-async-requests-pattern.html
 * @see https://medium.datadriveninvestor.com/aborting-cancelling-requests-with-fetch-or-axios-db2e93825a36
 */

import axios from "axios";

/**
 * Сохраненные токены для отмены ajax запросов, чтобы предотвратить накапливание долгих запросов.
 *
 * @type {Map<String, Object>}
 */
const CANCEL_SOURCES = new Map();

/**
 * Общий механизм обработки ошибок в запросах на события.
 * Необходим в момент обработки исключений.
 */
export function handleErrorInRequest(error) {
  if (axios.isCancel(error)) {
    // Ошибки по прерванным запросам выбрасывают пустое исключение, чтобы компонент смог проигнорировать их.
    throw null;
  }
  throw error;
}

/**
 * Функция отправит сигнал на прерывание повторного http запроса к API выполняемого через axios,
 * а так же создаст и вернет новый токен для возможности отмены следующего запроса.
 *
 * Прерывание повторно отправленных запросов и создание токенов отмены для новых происходит в рамках одного строкового ключа.
 * Приемлемым вариантом может быть название vuex action, т.к. именно в action осуществляется обращение к API для одной цели.
 *
 * @param {String} cancelTokenKey Ключ по которому в общем хранилище будет записан axios cancelSource
 * @return {Object|null}
 */
export function cancelRequestOnReentry(cancelTokenKey) {
  if (!cancelTokenKey) {
    return null;
  }
  const newCancelSource = axios.CancelToken.source(),
    prevCancelSource = CANCEL_SOURCES.get(cancelTokenKey);
  prevCancelSource && prevCancelSource.cancel("Operation canceled by the user.");
  CANCEL_SOURCES.set(cancelTokenKey, newCancelSource);
  return newCancelSource.token;
}
